import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Input,
  Select,
  notification,
  Form,
  Row,
  Col,
  Tooltip,
  Modal,
  Button,
} from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import PageContentHeader from "../../../components/common/PageContentHeader";
import Title from "antd/es/skeleton/Title";
import { getAllocationRequestsListService } from "../../../services/admin-services";
import {
  getAreasInStockService,
  getVehicleModelService,
  getLeadRequestTypesService,
} from "../../../services/master-services";
import { REQUEST_TYPE_CATEGORIES } from "../../../utills/constant";
import { openLeadViewInNewWindow } from "../../../utills/helpers";

const { Search } = Input;

const AllocationRequests = () => {
  const { isAdmin } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    area: null,
    model: null,
    physical_location: null,
    request_type: null,
    status: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Master data
  const [areaOptions, setAreaOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [physicalLocationOptions, setPhysicalLocationOptions] = useState([]);
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);
  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");

  const fetchAllocationRequests = async (
    page = pagination.current,
    perPage = pagination.pageSize,
    searchTerm = search,
    filterParams = filters
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add filter parameters
      if (filterParams.area) {
        params.customer_area_id = filterParams.area;
      }
      if (filterParams.model) {
        params.model_id = filterParams.model;
      }
      if (filterParams.physical_location) {
        params.physical_location_id = filterParams.physical_location;
      }
      if (filterParams.request_type) {
        params.request_type_id = filterParams.request_type;
      }
      if (filterParams.status !== undefined && filterParams.status !== null) {
        params.approval_status = filterParams.status;
      }
      const res = await getAllocationRequestsListService(params);
      const list = res?.data?.data?.list_data || [];
      const total = res?.data?.data?.total_records ?? 0;

      const rows = Array.isArray(list)
        ? list.map((item) => {
          return {
            key: item.id,
            id: item.id,
            lead_id: item.lead_id || "-",
            lead_number: item.lead_id ? `${item.lead_id}` : "-",
            customer_name: item.customer_name || "-",
            vehicle: item.vehicle || "-",
            customer_area: item.customer_area || "-",
            chassis_number: item.chassis_number || "-",
            stock_status: item.stock_status || "-",
            stock_age: item.stock_age || 0,
            physical_location_name: item.physical_location_name || "-",
            request_type_name: item.request_type_name || "-",
            request_creation_remarks: item.request_creation_remarks || "-",
            request_status:
              item.request_status || item.approval_status_name || "-",
            approval_status: item.approval_status,
            updated_at: item.updated_at || "-",
          };
        })
        : [];

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
    } catch (error) {
      notification.error({
        message: "Failed to load allocation requests",
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllocationRequests();
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasterData = async () => {
    try {
      // Load Areas (for Customer Area and Physical Location)
      const areasRes = await getAreasInStockService(true);
      const areas = areasRes?.data?.data || [];
      setAreaOptions(areas); // Area filter uses same areas
      setPhysicalLocationOptions(areas); // Physical location uses same areas

      // Load Models (Toyota make_id = 1)
      const modelsRes = await getVehicleModelService(1, true);
      const models = modelsRes?.data?.data || [];
      setModelOptions(models);

      // Load Request Types
      const requestTypesRes = await getLeadRequestTypesService();
      const requestTypes = requestTypesRes?.data?.data || [];
      // Filter for Allocation Request types only (id 1, 2)
      const allocationRequestTypes = requestTypes.filter((type) =>
        REQUEST_TYPE_CATEGORIES.ALLOCATION.includes(type.id)
      );
      setRequestTypeOptions(allocationRequestTypes);
    } catch (error) {
      console.error("Failed to load master data:", error);
    }
  };

  const handleSearch = (value) => {
    const trimmedValue = value?.trim() || "";
    setSearch(trimmedValue);
    fetchAllocationRequests(1, pagination.pageSize, trimmedValue, filters);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);
    // Reset to page 1 when filter changes
    fetchAllocationRequests(1, pagination.pageSize, search, newFilters);
  };

  // const handleReset = () => {
  //   setSearch('');
  //   setApprovalStatus(undefined);
  //   setCustomerAreaId(undefined);
  //   setModelId(undefined);
  //   setPhysicalLocationId(undefined);
  //   setRequestTypeId(undefined);
  //   fetchAllocationRequests(1, pagination.pageSize, '');
  // };

  // const formatDate = (dateString) => {
  //   if (!dateString || dateString === '-') return '-';
  //   return dayjs(dateString).format('DD/MM/YYYY');
  // };

  const columns = [
    {
      title: "Lead Number",
      dataIndex: "lead_number",
      key: "lead_number",
      render: (text, record) => {
        if (!text || text === "-" || !record.lead_id) return "-";
        return (
          <a
            onClick={() => {
              openLeadViewInNewWindow(isAdmin, record.lead_id);
            }}
            style={{ cursor: "pointer", color: "#1890ff" }}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => text || "-",
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => text || "-",
    },
    {
      title: "Customer Area",
      dataIndex: "customer_area",
      key: "customer_area",
      render: (text) => text || "-",
    },
    {
      title: "Chassis No.",
      dataIndex: "chassis_number",
      key: "chassis_number",
      render: (text) => text || "-",
    },
    {
      title: "Stock Status",
      dataIndex: "stock_status",
      key: "stock_status",
      render: (val) => {
        if (!val || val === "-") return "-";
        let color = "default";
        if (val === "Physical") color = "green";
        else if (val === "Transit") color = "blue";
        else if (val === "MDDP") color = "orange";
        return <Tag color={color}>{val}</Tag>;
      },
    },
    {
      title: "Stock Age",
      dataIndex: "stock_age",
      key: "stock_age",
      render: (val) => (val || val === 0 ? val : "-"),
    },
    {
      title: "Physical Location",
      dataIndex: "physical_location_name",
      key: "physical_location_name",
      render: (text) => text || "-",
    },
    {
      title: "Request Type",
      dataIndex: "request_type_name",
      key: "request_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Remarks",
      dataIndex: "request_creation_remarks",
      key: "request_creation_remarks",
      width: 200,
      render: (text) => {
        if (!text || text === "-") return "-";

        const maxLength = 50;
        const isLongText = text.length > maxLength;
        const truncatedText = isLongText ? `${text.substring(0, maxLength)}...` : text;

        return (
          <div style={{ wordBreak: "break-word" }}>
            <Tooltip title={text} placement="topLeft">
              <span>{truncatedText}</span>
            </Tooltip>
            {isLongText && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: 8, height: "auto" }}
                onClick={() => {
                  setSelectedRemarks(text);
                  setRemarksModalVisible(true);
                }}
              >
                See more
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Request Status",
      dataIndex: "request_status",
      key: "request_status",
      render: (status, record) => {
        const statusValue = record.approval_status;
        let color = "default";
        if (statusValue === 0 || status === "Pending") color = "gold";
        else if (statusValue === 1 || status === "Approved") color = "green";
        else if (statusValue === 2 || status === "Rejected") color = "red";
        return <Tag color={color}>{status || "-"}</Tag>;
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
  ];

  return (
    <div className="user-data">
      <PageContentHeader title="Allocation Requests" />
      <div className="personal-detail">
        <div className="user-left d-flex align-items-center">
          <Title level={4} className="pe-2 pb-2">
            Requests &gt; Allocation Requests
          </Title>
        </div>
        <Card className="mb-4">
          <Form layout="vertical">
            {/* Filters and Search Row */}

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Search" style={{ marginBottom: 0 }}>
                  <Search
                    placeholder="Search by Lead Number, Customer, Vehicle"
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={handleSearch}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    className="form-control-ant"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Area" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Area"
                    allowClear
                    value={filters.area}
                    onChange={(val) => handleFilterChange("area", val)}
                    className="form-control-ant"
                    showSearch
                    optionFilterProp="children"
                  >
                    {areaOptions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Model" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Model"
                    allowClear
                    value={filters.model}
                    onChange={(val) => handleFilterChange("model", val)}
                    className="form-control-ant"
                    showSearch
                    optionFilterProp="children"
                  >
                    {modelOptions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.model_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item
                  label="Physical Location"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Physical Location"
                    allowClear
                    value={filters.physical_location}
                    onChange={(val) =>
                      handleFilterChange("physical_location", val)
                    }
                    className="form-control-ant"
                    showSearch
                    optionFilterProp="children"
                  >
                    {physicalLocationOptions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Request Type" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Request Type"
                    allowClear
                    value={filters.request_type}
                    onChange={(val) => handleFilterChange("request_type", val)}
                    className="form-control-ant"
                    showSearch
                    optionFilterProp="children"
                  >
                    {requestTypeOptions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.request_type}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Status" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Status"
                    allowClear
                    value={filters.status}
                    onChange={(val) => handleFilterChange("status", val)}
                    className="form-control-ant"
                  >
                    <Select.Option value={0}>Pending</Select.Option>
                    <Select.Option value={1}>Approved</Select.Option>
                    <Select.Option value={2}>Rejected</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          size="middle"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          onChange={(paginationConfig) => {
            const { current, pageSize } = paginationConfig;
            fetchAllocationRequests(current, pageSize, search, filters);
          }}
          scroll={{
            x: "max-content", // 🟢 makes it responsive horizontally
          }}
        />
      </div>
      <Modal
        title="Remarks"
        open={remarksModalVisible}
        onCancel={() => {
          setRemarksModalVisible(false);
          setSelectedRemarks("");
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setRemarksModalVisible(false);
              setSelectedRemarks("");
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
      >
        <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {selectedRemarks}
        </p>
      </Modal>
    </div>
  );
};

export default AllocationRequests;
